import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Search, RefreshCw, AlertTriangle, Users, ChevronRight } from 'lucide-react';
import { systemApi } from '../../../api/client';
import type { SystemOrg } from '../../../api/client';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function SystemOverviewPage() {
  const navigate = useNavigate();
  const [orgs, setOrgs]           = useState<SystemOrg[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [search, setSearch]       = useState('');

  const load = () => {
    setIsLoading(true);
    systemApi
      .listOrganizations()
      .then(setOrgs)
      .catch((err) => setError((err as Error).message ?? 'Failed to load organizations'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) =>
      o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q),
    );
  }, [orgs, search]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-100">Organizations</h2>
          <p className="text-sm text-gray-500 mt-1">
            All organizations in the system. Click an org to manage its members and settings.
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-surface-2 border border-surface-4 rounded px-2 py-1.5">
          <Building2 className="w-3.5 h-3.5" />
          {orgs.length} total
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or slug…"
            className="w-full pl-8 pr-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand/60 transition-colors"
          />
        </div>
        <button
          onClick={load}
          className="p-2 text-gray-500 hover:text-gray-300 hover:bg-surface-3 rounded-md transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8 px-5">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Loading organizations…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-gray-500 gap-2">
            <Building2 className="w-8 h-8 text-gray-700" />
            <p className="text-sm">
              {search ? 'No organizations match your search.' : 'No organizations found.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" role="table">
              <thead>
                <tr className="border-b border-surface-4">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Slug</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Members</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Created</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((org) => (
                  <tr
                    key={org.id}
                    onClick={() => navigate(`/settings/system/orgs/${org.id}`)}
                    className="border-b border-surface-4/50 last:border-0 hover:bg-surface-2/30 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 font-medium text-gray-200">{org.name}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono hidden sm:table-cell">{org.slug}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                        <Users className="w-3 h-3" />
                        {org.memberCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">{formatDate(org.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-gray-600 inline-block" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-600 text-center">
        {filtered.length} {filtered.length === 1 ? 'organization' : 'organizations'}
        {orgs.length !== filtered.length && ` of ${orgs.length}`}
      </p>
    </div>
  );
}
